import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { loadConfig } from './config';
import { initAuth } from './auth';
import './styles.css';

/**
 * Bootstrap sequence:
 *
 *   1. Fetch /config.json (runtime, same-origin).
 *   2. Initialise oidc-client-ts with the issuer + client id from config.
 *   3. Mount the React tree.
 *
 * We intentionally do steps 1+2 BEFORE rendering so that every
 * component downstream can assume `getManager()` is ready. The cost is
 * a blank page for a few hundred ms on cold load; the win is that the
 * whole app never has to guard against an uninitialised UserManager.
 *
 * Any failure in this sequence is fatal and shows a plain DOM error —
 * there's no React tree yet, so we don't have the usual <ErrorBoundary>.
 */
async function main(): Promise<void> {
  const root = document.getElementById('root');
  if (!root) throw new Error('#root not found in index.html');

  try {
    const cfg = await loadConfig();
    initAuth(cfg);

    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <BrowserRouter>
          <App config={cfg} />
        </BrowserRouter>
      </React.StrictMode>,
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[atlas-web] bootstrap failed:', err);
    root.innerHTML = `
      <div style="font-family: system-ui, sans-serif; max-width: 640px; margin: 4rem auto; padding: 0 1rem;">
        <h1>Project Atlas</h1>
        <p>Startup error. Check the console.</p>
        <pre style="background:#f3f3f3;padding:1rem;border-radius:4px;">${String(err)}</pre>
      </div>
    `;
  }
}

void main();
